import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_SERVER_URL } from 'js/constants/api';
import { getAccessToken } from 'js/reducers/session/selectors';
import { normalizeCapsuleList, type NormalizedCapsules } from 'js/schemas/capsule';
import type { CapsuleDto, CapsuleResponseWithKeys } from './capsules.endpoints';
import {
  createClusterMutationDefinition,
  deleteCapsuleMutationDefinition,
  getAllowedClustersByOrgQueryDefinition,
  getCapsuleQueryDefinition,
  parseCapsulesListResponse,
  updateCapsuleMutationDefinition,
  type AllowedClustersParams,
  type CreateClusterPayload,
  type UpdateCapsuleArg,
} from './capsules.endpoints';

/**
 * Shape read by `getAccessToken` (see js/reducers/session/selectors.js).
 * We do not use `ReturnType<typeof rootReducer>` here: js/reducers imports this API slice,
 * so importing the combined reducer would create a circular module dependency.
 */
type ReduxStateForAccessToken = {
  session: Record<string, unknown>;
  entities: {
    users?: Record<string, unknown>;
    orgs?: Record<string, unknown>;
  };
};

const TAG_CAPSULE = 'Capsule' as const;
const TAG_CAPSULE_LIST = 'CapsuleList' as const;
const TAG_TYPES = [TAG_CAPSULE, TAG_CAPSULE_LIST] as const;

export const capsulesApi = createApi({
  reducerPath: 'capsulesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_SERVER_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getAccessToken(getState() as ReduxStateForAccessToken);
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  keepUnusedDataFor: 120,
  tagTypes: TAG_TYPES,
  endpoints: (builder) => ({
    getCapsules: builder.query<NormalizedCapsules, AllowedClustersParams>({
      query: getAllowedClustersByOrgQueryDefinition,
      transformResponse: (raw: unknown) => {
        const capsuleDtos = parseCapsulesListResponse(raw);
        return normalizeCapsuleList(capsuleDtos);
      },
      providesTags: [TAG_CAPSULE_LIST],
    }),

    getCapsule: builder.query<CapsuleDto, string>({
      query: (id) => getCapsuleQueryDefinition(id),
      transformResponse: (raw: unknown) => raw as CapsuleDto,
      providesTags: (_result, _err, id) => [{ type: TAG_CAPSULE, id }],
    }),

    createCluster: builder.mutation<CapsuleResponseWithKeys, CreateClusterPayload>({
      query: (body) => createClusterMutationDefinition(body),
      transformResponse: (raw: unknown) => raw as CapsuleResponseWithKeys,
      invalidatesTags: [TAG_CAPSULE_LIST],
    }),

    updateCapsule: builder.mutation<CapsuleDto, UpdateCapsuleArg>({
      query: (arg) => updateCapsuleMutationDefinition(arg),
      transformResponse: (raw: unknown) => raw as CapsuleDto,
      invalidatesTags: (_result, _err, updateArg) => {
        const capsuleId = updateArg.id;
        return [{ type: TAG_CAPSULE, id: capsuleId }, TAG_CAPSULE_LIST];
      },
    }),

    deleteCapsule: builder.mutation<void, string>({
      query: (id) => deleteCapsuleMutationDefinition(id),
      invalidatesTags: (_result, _err, id) => [{ type: TAG_CAPSULE, id }, TAG_CAPSULE_LIST],
    }),
  }),
});

/**
 * Note For Dev:
 * - Export here what you need to consume, treat these as the source of truth for the capsules API.
 * - Do not import `capsulesApi` in feature code, use the exports instead.
 */

export const resetCapsulesApiState = capsulesApi.util.resetApiState;

export const invalidateCapsuleListTags = () => capsulesApi.util.invalidateTags(['CapsuleList']);

export const useFetchCapsules = () => {
  const [fetchCapsules, result] = capsulesApi.useLazyGetCapsulesQuery();
  return { fetchCapsules, ...result };
};

export const useFetchCapsule = () => {
  const [fetchCapsule, result] = capsulesApi.useLazyGetCapsuleQuery();
  return { fetchCapsule, ...result };
};

export const useCreateCluster = () => {
  const [createCluster, result] = capsulesApi.useCreateClusterMutation();
  return { createCluster, ...result };
};

export const useUpdateCapsule = () => {
  const [updateCapsule, result] = capsulesApi.useUpdateCapsuleMutation();
  return { updateCapsule, ...result };
};

export const useDeleteCapsule = () => {
  const [deleteCapsule, result] = capsulesApi.useDeleteCapsuleMutation();
  return { deleteCapsule, ...result };
};
